import { t, Trans } from '@lingui/macro';
import { noddiAsync, URLKeys } from 'noddi-async';
import { BOOKING_TYPE } from 'noddi-types';
import { getLongAddressName, NoddiBasicCard, NoddiDummyButton } from 'noddi-ui-web';
import { useAuthContext } from 'noddi-web';
import { useNavigate } from 'react-router-dom';
import { AddressPicker } from '../../../components/Address/AddressPicker';
import { ApiErrorPageWithTrans } from '../../../components/ApiErrorPageWithTrans';
import { BookingScreen } from '../../../components/BookingFlow/BookingScreen';
import { NoddiLoadingScreenWithTrans } from '../../../components/NoddiLoadingScreenWithTrans';
import { GoogleTrackingService } from '../../../services/GoogleTrackingService';
import { GtmEvents } from '../../../types/gtmTracking';
import { useBookingActions, useBookingAddress, useBookingType } from '../bookingStore';
import { carStepPath, tireHotelCancelStepPath } from '../helpers/bookingFlowPaths';
import { useIsStepValid } from '../helpers/useStepCompletion';

export const Address = () => {
  const { setSelectedAddress } = useBookingActions();
  const selectedAddress = useBookingAddress();
  const { isAddressStepValid } = useIsStepValid();
  const bookingType = useBookingType();
  const navigate = useNavigate();

  const { currentUserGroupId: userGroupId, isLoggedIn } = useAuthContext();

  const {
    data: savedAddresses,
    isLoading: isLoadingSavedAddresses,
    error: savedAddressesError
  } = noddiAsync.useGet({
    type: URLKeys.getUserGroupAddressesValidForNewBooking,
    input: { id: userGroupId! },
    queryConfig: { enabled: !!userGroupId && isLoggedIn, staleTime: Infinity }
  });

  if (isLoadingSavedAddresses) {
    return <NoddiLoadingScreenWithTrans />;
  }

  if (savedAddressesError) {
    return <ApiErrorPageWithTrans error={savedAddressesError} />;
  }

  const isCancelTireHotelBooking = bookingType === BOOKING_TYPE.CANCEL_TIRE_HOTEL;

  const navigateToNextStep = () => {
    GoogleTrackingService.trackEvent(GtmEvents.viewItem);

    isCancelTireHotelBooking ? navigate(tireHotelCancelStepPath) : navigate(carStepPath);
  };

  return (
    <BookingScreen
      title={t`Where do you need our help?`}
      onNextClicked={navigateToNextStep}
      disableNextButton={!isAddressStepValid}
      hideShoppingCart
      hideBackButton
    >
      <>
        <AddressPicker
          setSelectedAddress={setSelectedAddress}
          selectedAddress={selectedAddress}
          showBrandsAnnouncement
          isLoading={isLoadingSavedAddresses}
        />

        {!!savedAddresses?.length && !selectedAddress && (
          <div className='mt-8 flex flex-col gap-2'>
            <span className='font-semibold'>
              <Trans>Saved addresses</Trans>
            </span>

            <div className='flex flex-col gap-4'>
              {savedAddresses.map((savedAddress) => (
                <NoddiBasicCard
                  onClick={() => {
                    setSelectedAddress(savedAddress);
                    navigateToNextStep();
                  }}
                  key={savedAddress.id}
                  size='small'
                  className='flex items-center justify-between gap-4'
                >
                  <span>{getLongAddressName(savedAddress)}</span>
                  <NoddiDummyButton endIcon='ArrowRight' variant='secondary' size='small'>
                    <Trans>Choose</Trans>
                  </NoddiDummyButton>
                </NoddiBasicCard>
              ))}
            </div>
          </div>
        )}
      </>
    </BookingScreen>
  );
};
